'use client';
import { useState, useEffect } from 'react';
import axios from 'axios';
import AirtableButtonSettingsModal from './AirtableButtonSettingsModal';
import { useDropzone } from 'react-dropzone';
import ToggleSwitch from '../ToggleSwitch';

const AirtableIntegration = ({ userId, buttonColor, onBackToMenu }) => {
  const [airtableConfig, setAirtableConfig] = useState({
    enabled: false,
    airtableUrl: 'https://api.airtable.com/v0/',
    clientId: '', // baseID
    tableId: '', // tableID
    buttonTextField1: '', // FirstName field
    buttonTextField2: '', // LastName field
    videoLinkField: '', // Video field
    sortField: '', // Sort field
    sortOrder: 'asc', // Ascending or Descending
    buttonsPerPage: 6,
    authToken: '',
    backButtonText: 'BACK',
    nextButtonText: 'NEXT',
  });

  // Button Settings State
  const [buttonSettings, setButtonSettings] = useState({
    backButtonSettings: {
      buttonText: 'BACK',
      cornerRadius: 5,
      height: 50,
      width: 150,
      outlineColor: '#000000',
      outlineThickness: 1,
      fillColor: '#ffffff',
      font: 'Arial',
      fontColor: '#000000',
      fontSize: 16,
      shadow: {
        angle: 125,
        size: 10,
        distance: 15,
        alpha: 0.2,
        color: '#000000',
      },
      highlight: {
        borderColor: '#8f8f8f',
        fillColor: '#000000',
        fontColor: '#ffffff',
      },
      clickSound: '',
      clickSoundFilename: null,
      clickSoundVolume: 1,
      textAlign: 'center',
      marginBottom: 2,
      lineHeight: 1,
      padding: 10,
      backButtonMedia: {
        fileName: '',
        fileUrl: '',
        enabled: false,
      },
    },
    nextButtonSettings: {
      buttonText: 'NEXT',
      cornerRadius: 5,
      height: 50,
      width: 150,
      outlineColor: '#000000',
      outlineThickness: 1,
      fillColor: '#ffffff',
      font: 'Arial',
      fontColor: '#000000',
      fontSize: 16,
      shadow: {
        angle: 125,
        size: 10,
        distance: 15,
        alpha: 0.2,
        color: '#000000',
      },
      highlight: {
        borderColor: '#8f8f8f',
        fillColor: '#000000',
        fontColor: '#ffffff',
      },
      clickSound: '',
      clickSoundFilename: null,
      clickSoundVolume: 1,
      textAlign: 'center',
      marginBottom: 2,
      lineHeight: 1,
      padding: 10,
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [showDataMapping, setShowDataMapping] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [networkError, setNetworkError] = useState(null);
  const [configExists, setConfigExists] = useState(false);
  const [showButtonSettingsModal, setShowButtonSettingsModal] = useState(false);
  const [backButtonMediaOnVideo, setBackButtonMediaOnVideo] = useState();
  const [uploadingStates, setUploadingStates] = useState(false);

  // Mock toast functions - replace with your actual implementation
  const showMsgSuccessToast = message => console.log('Success:', message);
  const showErrorToast = message => console.log('Error:', message);

  useEffect(() => {
    if (userId) {
      fetchAirtableConfig();
      fetchButtonSettings();
    }
  }, [userId]);

  const fetchAirtableConfig = async () => {
    if (!userId) {
      setIsInitialLoading(false);
      return;
    }

    setIsInitialLoading(true);
    try {
      const response = await axios.get(`/api/airtable-config/${userId}`);

      if (response.data && response.data.success) {
        const { isAirtableEnabled, config } = response.data.data;

        if (config && Object.keys(config).length > 0) {
          setAirtableConfig(prev => ({
            ...prev,
            ...config,
          }));
          setConfigExists(true);

          // If config is enabled and has required fields, show success status
          if (config.enabled && config.clientId && config.tableId && config.authToken) {
            setConnectionStatus('success');
          }
        } else {
          setConfigExists(false);
        }
      } else {
        setConfigExists(false);
      }
    } catch (error) {
      console.error('Error fetching Airtable config:', error);
      setConfigExists(false);

      // Only show error if it's not a 404 (no config found)
      if (error.response?.status !== 404) {
        showErrorToast(
          'Failed to load existing configuration: ' +
            (error.response?.data?.message || error.message)
        );
      }
    } finally {
      setIsInitialLoading(false);
    }
  };

  const fetchButtonSettings = async () => {
    try {
      console.log('Fetching button settings for userId:', userId);
      const response = await axios.get(`/api/airtable-button-settings/${userId}`);

      console.log('Button settings response:', response.data);

      if (response.data && response.data.success && response.data.data) {
        console.log('Setting button settings from API:', response.data.data);
        setButtonSettings(response.data.data);
      } else {
        console.log('No button settings found, using defaults');
      }
    } catch (error) {
      console.error('Error fetching button settings:', error);
      // Use default settings if fetch fails
    }
  };

  const handleInputChange = (field, value) => {
    setAirtableConfig(prev => ({
      ...prev,
      [field]: value,
    }));

    // Reset connection status when critical fields change
    if (['clientId', 'tableId', 'authToken'].includes(field)) {
      setConnectionStatus(null);
      setShowDataMapping(false);
      setShowPreview(false);
      setAvailableFields([]);
      setPreviewData([]);
    }
  };

  const handleButtonSettingChange = (buttonType, key, value) => {
    console.log(`Changing ${buttonType} button setting: ${key} = ${value}`);

    const keys = key.split('.');

    setButtonSettings(prev => {
      const newSettings = { ...prev };
      const buttonSettings = { ...newSettings[`${buttonType}ButtonSettings`] };

      if (keys.length === 1) {
        buttonSettings[keys[0]] = value;
      } else if (keys.length === 2) {
        buttonSettings[keys[0]] = {
          ...buttonSettings[keys[0]],
          [keys[1]]: value,
        };
      }

      newSettings[`${buttonType}ButtonSettings`] = buttonSettings;
      console.log('Updated button settings:', newSettings);
      return newSettings;
    });
  };

  const handleCheckboxChange = checked => {
    setAirtableConfig(prev => ({
      ...prev,
      enabled: checked,
    }));

    // Reset states when disabling
    if (!checked) {
      setConnectionStatus(null);
      setShowDataMapping(false);
      setShowPreview(false);
      setAvailableFields([]);
      setPreviewData([]);
    }
  };

  // Extract field names from API response
  const extractFieldNames = records => {
    const fieldSet = new Set();
    records.forEach(record => {
      if (record.fields) {
        Object.keys(record.fields).forEach(fieldName => {
          fieldSet.add(fieldName);
        });
      }
    });
    return Array.from(fieldSet).sort();
  };

  // Get video URL from Video field (handles array structure)
  const getVideoUrl = videoField => {
    if (Array.isArray(videoField) && videoField.length > 0) {
      return videoField[0].url;
    }
    return videoField || '';
  };

  // Generate button text based on firstName and lastName logic
  const generateButtonText = (record, field1, field2) => {
    const firstName = record.fields[field1] || '';
    const lastName = record.fields[field2] || '';
    if (lastName.trim()) {
      return `${firstName} ${lastName}`.trim();
    }
    return firstName.trim() || 'Unnamed';
  };

  const testConnectionAndFetchData = async () => {
    if (!airtableConfig.clientId || !airtableConfig.tableId || !airtableConfig.authToken) {
      showErrorToast(
        'Please fill in Client ID (Base ID), Table ID, and Authentication Token to fetch data.'
      );
      return;
    }

    setIsLoading(true);
    setNetworkError(null);
    setConnectionStatus(null);

    try {
      const response = await axios.post('/api/airtable-data-api', {
        userId: userId,
        config: airtableConfig,
      });

      console.log('response', response.data);

      if (response.data && Array.isArray(response.data)) {
        setConnectionStatus('success');
        showMsgSuccessToast('Connection successful! Data fetched from Airtable.');

        // Extract available fields and set preview data
        const fields = extractFieldNames(response.data);
        setAvailableFields(fields);
        setPreviewData(response.data);
        setShowDataMapping(true);
        setShowPreview(false); // Hide preview until mapping is done
      } else {
        setConnectionStatus('error');
        showErrorToast('Connection failed: ' + (response.data?.message || 'Unknown error'));
      }
    } catch (error) {
      setConnectionStatus('error');
      setNetworkError(error.response?.data?.networkError || 'Network connection failed');
      showErrorToast('Connection failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  const generatePreview = () => {
    if (!airtableConfig.buttonTextField1 || !airtableConfig.videoLinkField) {
      showErrorToast('Please select Button Text Field 1 and Video Link Field to generate preview.');
      return;
    }

    // Filter out records without required fields
    const validRecords = previewData.filter(record => {
      const hasButtonText =
        record.fields[airtableConfig.buttonTextField1] ||
        record.fields[airtableConfig.buttonTextField2];
      const hasVideo = record.fields[airtableConfig.videoLinkField];
      return hasButtonText && hasVideo;
    });

    if (validRecords.length === 0) {
      showErrorToast('No valid records found with the selected field mapping.');
      return;
    }

    setPreviewData(validRecords);
    setShowPreview(true);
    showMsgSuccessToast(`Preview generated with ${validRecords.length} valid records!`);
  };

  const saveConfiguration = async () => {
    // if (!airtableConfig.enabled) {
    //   showErrorToast("Please enable Airtable integration first.")
    //   return
    // }

    if (!airtableConfig.clientId || !airtableConfig.tableId || !airtableConfig.authToken) {
      showErrorToast(
        'Please fill in required fields: Client ID, Table ID, and Authentication Token.'
      );
      return;
    }

    if (!airtableConfig.buttonTextField1 || !airtableConfig.videoLinkField) {
      showErrorToast(
        'Please complete field mapping: Button Text Field 1 and Video Link Field are required.'
      );
      return;
    }

    setIsLoading(true);
    try {
      console.log('Saving configuration with button settings:', buttonSettings);

      // Save Airtable config
      const configResponse = await axios.post('/api/save-airtable-config', {
        userId: userId,
        config: airtableConfig,
      });

      // Save button settings
      const buttonResponse = await axios.post('/api/save-airtable-button-settings', {
        userId: userId,
        backButtonSettings: buttonSettings.backButtonSettings,
        nextButtonSettings: buttonSettings.nextButtonSettings,
      });

      console.log('Config response:', configResponse.data);
      console.log('Button response:', buttonResponse.data);

      if (
        configResponse.data &&
        configResponse.data.success &&
        buttonResponse.data &&
        buttonResponse.data.success
      ) {
        showMsgSuccessToast('Airtable configuration and button settings saved successfully!');
        setConfigExists(true);

        // Optionally refresh the config to get the latest saved data
        setTimeout(() => {
          fetchAirtableConfig();
          fetchButtonSettings();
        }, 1000);
      } else {
        showErrorToast(
          'Failed to save configuration: ' +
            (configResponse.data?.message || buttonResponse.data?.message || 'Unknown error')
        );
      }
    } catch (error) {
      console.error('Save error:', error);
      showErrorToast(
        'Failed to save configuration: ' + (error.response?.data?.message || error.message)
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleButtonSettingsSave = async () => {
    try {
      const response = await axios.post('/api/save-airtable-button-settings', {
        userId: userId,
        backButtonSettings: buttonSettings.backButtonSettings,
        nextButtonSettings: buttonSettings.nextButtonSettings,
      });

      if (response.data && response.data.success) {
        showMsgSuccessToast('Button settings saved successfully!');
        fetchButtonSettings(); // Refresh settings
      } else {
        showErrorToast(
          'Failed to save button settings: ' + (response.data?.message || 'Unknown error')
        );
      }
    } catch (error) {
      console.error('Save button settings error:', error);
      showErrorToast(
        'Failed to save button settings: ' + (error.response?.data?.message || error.message)
      );
    }
  };

  // Sort preview data based on configuration
  const sortedPreviewData = [...previewData].sort((a, b) => {
    if (!airtableConfig.sortField) return 0;

    const fieldA = a.fields[airtableConfig.sortField] || 0;
    const fieldB = b.fields[airtableConfig.sortField] || 0;

    if (airtableConfig.sortOrder === 'desc') {
      return fieldB - fieldA;
    }
    return fieldA - fieldB;
  });

  // Calculate pagination
  const totalPages = Math.ceil(sortedPreviewData.length / airtableConfig.buttonsPerPage);
  const [currentPage, setCurrentPage] = useState(1);
  const startIndex = (currentPage - 1) * airtableConfig.buttonsPerPage;
  const endIndex = startIndex + airtableConfig.buttonsPerPage;
  const currentPageData = sortedPreviewData.slice(startIndex, endIndex);

  const createDropHandler = setMedia => acceptedFiles => {
    if (acceptedFiles.length > 0) {
      setMedia(acceptedFiles[0]);
    }
  };

  // Common upload handler
  const handleUpload = async file => {
    if (!file) {
      showErrorToast(`Please select a file!`);
      return;
    }

    // Set the specific position to uploading
    setUploadingStates(true);

    try {
      // Step 1: Get Pre-signed URL from the Backend
      const response = await axios.get('/generate-signed-url', {
        params: {
          fileName: file.name,
          fileType: file.type,
        },
      });

      const { url } = response.data;

      // Step 2: Upload the File to S3
      await axios.put(url, file, {
        headers: {
          'Content-Type': file.type,
        },
      });

      // Step 3: Save Uploaded File Information
      const fileUrl = url.split('?')[0];

      console.log('fileUrl', fileUrl);

      setButtonSettings(prevSettings => ({
        ...prevSettings,
        backButtonSettings: {
          ...prevSettings.backButtonSettings,
          backButtonMedia: {
            fileName: file.name,
            fileUrl: fileUrl,
            enabled: true,
          },
        },
      }));
      setUploadingStates(false);
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const handleToggleChangeMedia = on => {
    setButtonSettings(prevSettings => ({
      ...prevSettings,
      backButtonSettings: {
        ...prevSettings.backButtonSettings,
        backButtonMedia: {
          ...prevSettings.backButtonSettings.backButtonMedia,
          enabled: on,
        },
      },
    }));
  };

  // Show loading spinner while fetching initial config
  if (isInitialLoading) {
    return (
      <div className="main-container">
        <div className="pd-ltr-20 xs-pd-20-10">
          <div className="min-height-200px d-flex justify-content-center align-items-center">
            <div className="text-center">
              <div className="spinner-border" role="status" style={{ color: buttonColor }}>
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-3">Loading Airtable configuration...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container2">
      <div className="pd-ltr-20 xs-pd-20-10">
        <div className="min-height-200px">
          <div className="page-header">
            <div className="row">
              <div className="col-md-6 col-sm-12">
                <div className="title">
                  <h4>Airtable Integration Configuration</h4>
                  {configExists && (
                    <small className="text-success">✅ Existing configuration loaded</small>
                  )}
                </div>
              </div>
              <div className="col-md-6 col-sm-12">
                <button
                  type="button"
                  onClick={onBackToMenu}
                  style={{ float: 'right', backgroundColor: buttonColor }}
                  className="btn btn-success"
                >
                  {`<< Back to Menu System`}
                </button>
              </div>
            </div>
          </div>

          {/* Network Error Display */}
          {networkError && (
            <div className="alert alert-danger">
              <h5>Network Connection Failed</h5>
              <p>{networkError}</p>
              <small>Please check your internet connection and try again.</small>
            </div>
          )}

          <div className="dashboard-container">
            <div className="row">
              <div className="col-md-8">
                <div className="card">
                  <div className="card-header">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5>Airtable Integration Settings</h5>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          style={{
                            width: '48px',
                            marginLeft: '-56px',
                            height: '23px',
                            marginTop: '0',
                          }}
                          type="checkbox"
                          id="enableAirtable"
                          checked={airtableConfig.enabled}
                          onChange={e => handleCheckboxChange(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="enableAirtable">
                          Enable Airtable Integration
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    <form>
                      {/* Basic Configuration */}
                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Airtable URL *</label>
                            <input
                              type="url"
                              className="form-control"
                              placeholder="https://api.airtable.com/v0/"
                              value={airtableConfig.airtableUrl}
                              onChange={e => handleInputChange('airtableUrl', e.target.value)}
                              disabled={!airtableConfig.enabled}
                            />
                            <small className="text-muted">Base Airtable API URL</small>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Client ID (Base ID) *</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="appXXXXXXXXXXXXXX"
                              value={airtableConfig.clientId}
                              onChange={e => handleInputChange('clientId', e.target.value)}
                              disabled={!airtableConfig.enabled}
                            />
                            <small className="text-muted">Your Airtable Base ID</small>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Table ID *</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="tblXXXXXXXXXXXXXX"
                              value={airtableConfig.tableId}
                              onChange={e => handleInputChange('tableId', e.target.value)}
                              disabled={!airtableConfig.enabled}
                            />
                            <small className="text-muted">Your Airtable Table ID</small>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Authentication Token *</label>
                            <input
                              type="password"
                              className="form-control"
                              placeholder="patXXXXXXXXXXXXXX"
                              value={airtableConfig.authToken}
                              onChange={e => handleInputChange('authToken', e.target.value)}
                              disabled={!airtableConfig.enabled}
                            />
                            <small className="text-muted">
                              Your Airtable Personal Access Token
                            </small>
                          </div>
                        </div>
                      </div>

                      {/* Fetch Data Button */}
                      <div className="row mt-3">
                        <div className="col-md-12">
                          <button
                            type="button"
                            className="btn btn-primary"
                            style={{ backgroundColor: buttonColor, borderColor: buttonColor }}
                            onClick={testConnectionAndFetchData}
                            disabled={isLoading || !airtableConfig.enabled}
                          >
                            {isLoading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm mr-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Fetching Data...
                              </>
                            ) : (
                              'Fetch Data from Airtable'
                            )}
                          </button>
                        </div>
                      </div>

                      {connectionStatus && (
                        <div
                          className={`alert ${connectionStatus === 'success' ? 'alert-success' : 'alert-danger'} mt-3`}
                        >
                          {connectionStatus === 'success'
                            ? '✅ Connection successful! Data fetched from Airtable.'
                            : '❌ Connection failed. Please check your configuration and try again.'}
                        </div>
                      )}

                      {/* Data Mapping Section */}
                      {showDataMapping && availableFields.length > 0 && (
                        <>
                          <hr className="mt-4 mb-4" />
                          <h6>Data Mapping</h6>
                          <div className="alert alert-info">
                            <strong>Available Fields:</strong> {availableFields.join(', ')}
                          </div>
                          <div className="row">
                            <div className="col-md-4">
                              <div className="form-group">
                                <label>Button Text Field 1*</label>
                                <select
                                  className="form-control"
                                  value={airtableConfig.buttonTextField1}
                                  onChange={e =>
                                    handleInputChange('buttonTextField1', e.target.value)
                                  }
                                  disabled={!airtableConfig.enabled}
                                  style={{ height: '58px' }}
                                >
                                  <option value="">Select Field</option>
                                  {availableFields.map(field => (
                                    <option key={field} value={field}>
                                      {field}
                                    </option>
                                  ))}
                                </select>
                                <small className="text-muted">Primary field for button text</small>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="form-group">
                                <label>Button Text Field 2</label>
                                <select
                                  className="form-control"
                                  value={airtableConfig.buttonTextField2}
                                  onChange={e =>
                                    handleInputChange('buttonTextField2', e.target.value)
                                  }
                                  disabled={!airtableConfig.enabled}
                                  style={{ height: '58px' }}
                                >
                                  <option value="">Select Field (Optional)</option>
                                  {availableFields.map(field => (
                                    <option key={field} value={field}>
                                      {field}
                                    </option>
                                  ))}
                                </select>
                                <small className="text-muted">
                                  Secondary field for button text
                                </small>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="form-group">
                                <label>Video Link Field *</label>
                                <select
                                  className="form-control"
                                  value={airtableConfig.videoLinkField}
                                  onChange={e =>
                                    handleInputChange('videoLinkField', e.target.value)
                                  }
                                  disabled={!airtableConfig.enabled}
                                  style={{ height: '58px' }}
                                >
                                  <option value="">Select Field</option>
                                  {availableFields.map(field => (
                                    <option key={field} value={field}>
                                      {field}
                                    </option>
                                  ))}
                                </select>
                                <small className="text-muted">Field containing video URLs</small>
                              </div>
                            </div>
                          </div>

                          {/* Sorting and Pagination */}
                          <div className="row">
                            <div className="col-md-4">
                              <div className="form-group">
                                <label>Sort Field</label>
                                <select
                                  className="form-control"
                                  value={airtableConfig.sortField}
                                  onChange={e => handleInputChange('sortField', e.target.value)}
                                  disabled={!airtableConfig.enabled}
                                  style={{ height: '58px' }}
                                >
                                  <option value="">Select Field (Optional)</option>
                                  {availableFields.map(field => (
                                    <option key={field} value={field}>
                                      {field}
                                    </option>
                                  ))}
                                </select>
                                <small className="text-muted">Field to sort records by</small>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="form-group">
                                <label>Sort Order</label>
                                <select
                                  className="form-control"
                                  value={airtableConfig.sortOrder}
                                  onChange={e => handleInputChange('sortOrder', e.target.value)}
                                  disabled={!airtableConfig.enabled}
                                  style={{ height: '58px' }}
                                >
                                  <option value="asc">Ascending</option>
                                  <option value="desc">Descending</option>
                                </select>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="form-group">
                                <label>Buttons per Page</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  min="1"
                                  max="20"
                                  value={airtableConfig.buttonsPerPage}
                                  onChange={e =>
                                    handleInputChange(
                                      'buttonsPerPage',
                                      Number.parseInt(e.target.value)
                                    )
                                  }
                                  disabled={!airtableConfig.enabled}
                                />
                                <small className="text-muted">
                                  Number of buttons per menu page
                                </small>
                              </div>
                            </div>
                          </div>

                          {/* Generate Preview Button */}
                          <div className="row mt-3">
                            <div className="col-md-12">
                              <button
                                type="button"
                                className="btn btn-warning mr-2"
                                onClick={generatePreview}
                                disabled={!airtableConfig.enabled}
                              >
                                Generate Preview
                              </button>
                            </div>
                          </div>
                        </>
                      )}

                      {/* Navigation Button Text */}
                      <h6 className="mt-4">Navigation Button Text</h6>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Back Button Text</label>
                            <input
                              type="text"
                              className="form-control"
                              value={airtableConfig.backButtonText}
                              onChange={e => handleInputChange('backButtonText', e.target.value)}
                              disabled={!airtableConfig.enabled}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Next Button Text</label>
                            <input
                              type="text"
                              className="form-control"
                              value={airtableConfig.nextButtonText}
                              onChange={e => handleInputChange('nextButtonText', e.target.value)}
                              disabled={!airtableConfig.enabled}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="row ">
                        {/* Left Top Media */}
                        <div className="col-md-12 ">
                          <div
                            className={`form-group menu-card2 ${!buttonSettings.backButtonSettings.backButtonMedia?.enabled ? 'disabled' : ''}`}
                          >
                            <label>Back Button Media on Video Page:</label>
                            <ToggleSwitch
                              label="Enable"
                              isOn={buttonSettings.backButtonSettings.backButtonMedia?.enabled}
                              id={'backButtonMediaOnVideo'}
                              onChange={value => handleToggleChangeMedia(value)}
                            />

                            <MediaDropzone
                              onDrop={createDropHandler(setBackButtonMediaOnVideo)}
                              selectedFile={backButtonMediaOnVideo}
                            />

                            <UploadButton
                              uploading={uploadingStates}
                              onUpload={() => handleUpload(backButtonMediaOnVideo)}
                              buttonColor={buttonColor}
                            />
                            <UploadedUrl url={buttonSettings.backButtonSettings.backButtonMedia} />
                          </div>
                        </div>
                      </div>

                      {/* Button Settings Toggle */}
                      <div className="row mt-4">
                        <div className="col-md-12">
                          <button
                            type="button"
                            className="btn btn-info mr-2"
                            onClick={() => setShowButtonSettingsModal(true)}
                          >
                            Configure Button Styles
                          </button>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="row mt-4">
                        <div className="col-md-12">
                          <div className="btn-group flex-row flex gap-2" role="group">
                            <button
                              type="button"
                              className="btn btn-primary"
                              style={{ backgroundColor: buttonColor, borderColor: buttonColor }}
                              onClick={saveConfiguration}
                              // disabled={isLoading || !airtableConfig.enabled}
                              disabled={isLoading}
                            >
                              {isLoading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm mr-2"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  Saving...
                                </>
                              ) : (
                                'Save Configuration'
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>

                {/* Data Preview Table */}
                {showPreview && previewData.length > 0 && (
                  <div className="card mt-4">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h5>Airtable Data Preview</h5>
                      <div className="d-flex align-items-center">
                        <span className="badge badge-info mr-2">{previewData.length} Records</span>
                        <span className="badge badge-secondary">
                          Page {currentPage} of {totalPages}
                        </span>
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="table-responsive">
                        <table className="table table-striped table-hover">
                          <thead style={{ backgroundColor: buttonColor, color: 'white' }}>
                            <tr>
                              <th>Record ID</th>
                              <th>Button Text</th>
                              <th>Video Link</th>
                              {airtableConfig.sortField && <th>Sort Value</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {currentPageData.map((record, index) => (
                              <tr key={record.id}>
                                <td>
                                  <code className="text-muted">{record.id}</code>
                                </td>
                                <td>
                                  <strong>
                                    {generateButtonText(
                                      record,
                                      airtableConfig.buttonTextField1,
                                      airtableConfig.buttonTextField2
                                    )}
                                  </strong>
                                </td>
                                <td>
                                  <a
                                    href={getVideoUrl(record.fields[airtableConfig.videoLinkField])}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary"
                                  >
                                    🎥 Video Link
                                  </a>
                                </td>
                                {airtableConfig.sortField && (
                                  <td>
                                    <span className="badge badge-light">
                                      {record.fields[airtableConfig.sortField] || index + 1}
                                    </span>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination Preview */}
                      {totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                          <button
                            className="btn btn-secondary"
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            style={{
                              backgroundColor: currentPage === 1 ? '#6c757d' : buttonColor,
                              opacity: currentPage === 1 ? 0.5 : 1,
                            }}
                          >
                            {airtableConfig.backButtonText}
                          </button>
                          <span className="mx-3">
                            Showing {startIndex + 1}-{Math.min(endIndex, previewData.length)} of{' '}
                            {previewData.length} records
                          </span>
                          <button
                            className="btn btn-secondary"
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            style={{
                              backgroundColor: currentPage === totalPages ? '#6c757d' : buttonColor,
                              opacity: currentPage === totalPages ? 0.5 : 1,
                            }}
                          >
                            {airtableConfig.nextButtonText}
                          </button>
                        </div>
                      )}

                      {/* Menu Button Preview */}
                      <div className="mt-4">
                        <h6>Menu Button Preview (Page {currentPage})</h6>
                        <div className="row">
                          {currentPageData.map((record, index) => (
                            <div key={record.id} className="col-md-4 mb-2">
                              <button
                                className="btn btn-outline-primary btn-block"
                                style={{
                                  borderColor: buttonColor,
                                  color: buttonColor,
                                  fontSize: '0.9rem',
                                  padding: '8px 12px',
                                }}
                                onClick={() => {
                                  const videoUrl = getVideoUrl(
                                    record.fields[airtableConfig.videoLinkField]
                                  );
                                  if (videoUrl) {
                                    window.open(videoUrl, '_blank');
                                  }
                                }}
                              >
                                {generateButtonText(
                                  record,
                                  airtableConfig.buttonTextField1,
                                  airtableConfig.buttonTextField2
                                )}
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="col-md-4">
                <div className="card">
                  <div className="card-header">
                    <h5>Integration Status</h5>
                  </div>
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-3">
                      <div
                        className={`status-indicator ${
                          airtableConfig.enabled
                            ? connectionStatus === 'success'
                              ? 'success'
                              : 'warning'
                            : 'inactive'
                        }`}
                      ></div>
                      <span className="ml-2">
                        {!airtableConfig.enabled
                          ? 'Integration Disabled'
                          : connectionStatus === 'success'
                            ? 'Connected & Ready'
                            : 'Configuration Required'}
                      </span>
                    </div>

                    {showPreview && (
                      <div className="alert alert-info">
                        <strong>Preview Active!</strong>
                        <br />
                        <small>
                          • {previewData.length} records loaded
                          <br />• {totalPages} menu pages will be created
                          <br />• {airtableConfig.buttonsPerPage} buttons per page
                          <br />• Sorted by: {airtableConfig.sortField || 'Default'} (
                          {airtableConfig.sortOrder})
                        </small>
                      </div>
                    )}

                    <h6>Configuration Checklist:</h6>
                    <ul className="list-unstyled">
                      <li className={airtableConfig.enabled ? 'text-success' : 'text-muted'}>
                        {airtableConfig.enabled ? '✅' : '⭕'} Integration enabled
                      </li>
                      <li className={airtableConfig.clientId ? 'text-success' : 'text-muted'}>
                        {airtableConfig.clientId ? '✅' : '⭕'} Base ID configured
                      </li>
                      <li className={airtableConfig.tableId ? 'text-success' : 'text-muted'}>
                        {airtableConfig.tableId ? '✅' : '⭕'} Table ID configured
                      </li>
                      <li className={airtableConfig.authToken ? 'text-success' : 'text-muted'}>
                        {airtableConfig.authToken ? '✅' : '⭕'} Authentication token set
                      </li>
                      <li className={showDataMapping ? 'text-success' : 'text-muted'}>
                        {showDataMapping ? '✅' : '⭕'} Data fetched successfully
                      </li>
                      <li
                        className={airtableConfig.buttonTextField1 ? 'text-success' : 'text-muted'}
                      >
                        {airtableConfig.buttonTextField1 ? '✅' : '⭕'} Button text field 1 mapped
                      </li>
                      <li className={airtableConfig.videoLinkField ? 'text-success' : 'text-muted'}>
                        {airtableConfig.videoLinkField ? '✅' : '⭕'} Video link field mapped
                      </li>
                    </ul>

                    {availableFields.length > 0 && (
                      <div className="mt-3">
                        <h6>Available Fields ({availableFields.length}):</h6>
                        <small className="text-muted">
                          {availableFields.slice(0, 5).join(', ')}
                          {availableFields.length > 5 && '...'}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Button Settings Modal */}
      <AirtableButtonSettingsModal
        airtableConfig={airtableConfig}
        isOpen={showButtonSettingsModal}
        onClose={() => setShowButtonSettingsModal(false)}
        buttonSettings={buttonSettings}
        onSettingsChange={handleButtonSettingChange}
        buttonColor={buttonColor}
        onSave={handleButtonSettingsSave}
      />

      <style jsx>{`
        .status-indicator {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background-color: #dc3545;
        }
        .status-indicator.success {
          background-color: #28a745;
        }
        .status-indicator.warning {
          background-color: #ffc107;
        }
        .status-indicator.inactive {
          background-color: #6c757d;
        }
        .form-check-input:checked {
          background-color: ${buttonColor};
          border-color: ${buttonColor};
        }
        .table th {
          font-size: 0.85rem;
          font-weight: 600;
        }
        .table td {
          font-size: 0.9rem;
          vertical-align: middle;
        }
        code {
          font-size: 0.8rem;
        }
        .badge {
          font-size: 0.75rem;
        }
        .text-muted,
        .text-success {
          margin-bottom: 5px;
          margin-top: 5px;
        }
        .spinner-border-sm {
          width: 1rem;
          height: 1rem;
        }
      `}</style>
    </div>
  );
};

export default AirtableIntegration;

const MediaDropzone = ({ onDrop, selectedFile }) => {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
      'video/*': ['.mp4', '.mov', '.mp3'],
      'application/pdf': ['.pdf'],
    },
    multiple: false,
  });

  return (
    <div
      {...getRootProps()}
      style={{
        border: '2px dashed #cccccc',
        padding: '20px',
        borderRadius: '5px',
        textAlign: 'center',
        cursor: 'pointer',
      }}
    >
      <input {...getInputProps()} />
      {selectedFile ? (
        <p>{selectedFile.name}</p>
      ) : (
        <p>Drag and drop a file here, or click to select a file</p>
      )}
    </div>
  );
};

// Helper function to check if the file is an image
const getFileType = fileUrl => {
  if (!fileUrl) return 'blank'; // No URL provided

  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg'];
  const videoExtensions = ['.mp4', '.mov', '.avi', '.mkv', '.webm'];

  if (imageExtensions.some(ext => fileUrl.toLowerCase().endsWith(ext))) {
    return 'image';
  }
  if (videoExtensions.some(ext => fileUrl.toLowerCase().endsWith(ext))) {
    return 'video';
  }
  return 'other'; // Other file types or unsupported extensions
};

// UploadButton Component
const UploadButton = ({ uploading, onUpload, buttonColor }) => (
  <button
    onClick={onUpload}
    type="button"
    style={{
      marginTop: '20px',
      //   padding: "10px 20px",
      backgroundColor: buttonColor,
      // color: "#FFFFFF",
      border: 'none',
      borderRadius: '5px',
      cursor: 'pointer',
    }}
    disabled={uploading}
  >
    {uploading ? 'Uploading...' : 'Upload'}
  </button>
);

// UploadedUrl Component
const UploadedUrl = ({ url }) =>
  url ? (
    <div
      style={{
        marginTop: '20px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start', // Adjust alignment of child elements (optional)
        gap: '10px', // Add spacing between child elements (optional)
      }}
    >
      <div style={{ float: 'left', width: '70%', marginTop: '15px' }}>
        Uploaded File:{' '}
        <a
          href={url.fileUrl}
          style={{ fontWeight: 'bold' }}
          target="_blank"
          rel="noopener noreferrer"
        >
          {url.fileName}
        </a>
      </div>
      <div style={{ float: 'right', width: '30%', textAlign: 'right' }}>
        {getFileType(url.fileUrl) === 'image' ? (
          // Render image if it's an image file
          <a href={url.fileUrl} target="_blank" rel="noreferrer">
            <img src={url.fileUrl} style={{ maxWidth: '200px', maxHeight: '60px' }} alt="" />
          </a>
        ) : getFileType(url.fileUrl) === 'video' ? (
          // Render play button for video file
          <a href={url.fileUrl} target="_blank" rel="noreferrer">
            <img
              src="https://paulavatar.s3.us-east-2.amazonaws.com/play-button.png"
              style={{ maxWidth: '200px', maxHeight: '60px' }}
              alt="Play Video"
            />
          </a>
        ) : (
          // Fallback for other cases
          ''
        )}
      </div>
    </div>
  ) : null;
